import React, { useEffect, useState } from "react";
import { api, TIMEFRAMES } from "@/lib/api";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Send, Mail, Save, Loader2, Eye, EyeOff } from "lucide-react";
import { useApp, CURRENCIES_LIST } from "@/context/AppContext";
import { AccountDataSection } from "@/components/AccountDataSection";

export default function SettingsPage() {
  const { setAccountCurrency: setCtxAccountCur, setTimezone: setCtxTz } = useApp();
  const [tzList, setTzList] = useState([]);
  const [regions, setRegions] = useState([]);
  const [compModes, setCompModes] = useState({});
  const [s, setS] = useState({
    telegram_bot_token: "",
    telegram_chat_id: "",
    email_recipient: "",
    default_timeframe: "H1",
    watchlist: [],
    twelve_data_api_key: "",
    account_currency: "USD",
    timezone: "UTC",
    region: "global",
    compliance_mode: "none",
  });
  const [watchStr, setWatchStr] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);

  useEffect(() => {
    // Load supporting data
    Promise.all([
      axios.get(`${API}/regional/timezones`).then((r) => { setTzList(r.data.timezones || []); setRegions(r.data.regions || []); }).catch(() => {}),
      axios.get(`${API}/compliance/modes`).then((r) => setCompModes(r.data.modes || {})).catch(() => {}),
    ]);
    api.settings().then((d) => {
      setS({
        telegram_bot_token: "",
        telegram_bot_token_masked: d.telegram_bot_token_masked || "",
        telegram_chat_id: d.telegram_chat_id || "",
        email_recipient: d.email_recipient || "",
        default_timeframe: d.default_timeframe || "H1",
        watchlist: d.watchlist || [],
        twelve_data_api_key: "",
        twelve_data_api_key_masked: d.twelve_data_api_key_masked || "",
        account_currency: d.account_currency || "USD",
        timezone: d.timezone || "UTC",
        region: d.region || "global",
        compliance_mode: d.compliance_mode || "none",
      });
      setWatchStr((d.watchlist || []).join(", "));
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...s,
        watchlist: watchStr.split(",").map((x) => x.trim().toUpperCase()).filter(Boolean),
      };
      const d = await api.updateSettings(payload);
      setS((prev) => ({ ...prev, ...d, telegram_bot_token: "", twelve_data_api_key: "" }));
      setCtxAccountCur(d.account_currency);
      setCtxTz(d.timezone);
      toast.success("Paramètres enregistrés");
    } catch { toast.error("Impossible d'enregistrer"); }
    finally { setSaving(false); }
  };

  const test = async (ch) => {
    setTesting(ch);
    try {
      const res = await api.testNotify(ch);
      const okT = res.telegram?.ok !== false && (ch !== "email");
      const okE = res.email?.ok !== false && (ch !== "telegram");
      if (ch === "telegram") {
        if (res.telegram?.ok) toast.success("Message Telegram envoyé");
        else toast.error(`Telegram : ${res.telegram?.error || "échec"}`);
      } else if (ch === "email") {
        if (res.email?.ok) toast.success("Email envoyé");
        else toast.error(`Email : ${res.email?.error || "échec"}`);
      } else {
        toast.info(JSON.stringify(res));
      }
    } finally { setTesting(null); }
  };

  return (
    <div className="p-6 max-w-3xl">
      <h1 className="font-heading font-bold text-2xl tracking-tight mb-1" data-testid="settings-title">Paramètres & Notifications</h1>
      <p className="text-white/50 text-sm mb-6">Configurez comment l'algorithme vous alerte quand un ordre d'achat ou de vente se dessine.</p>

      <div className="panel p-5 space-y-5">
        <Section title="🌍 Compte, fuseau & compliance"
                 desc="Devise de votre COMPTE de trading réel (distincte de la devise d'affichage), fuseau horaire (pour les Kill Zones locales), région (weekend forex) et mode de conformité réglementaire.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label">Devise du compte de trading</label>
              <select className="field"
                value={s.account_currency}
                onChange={(e) => setS({ ...s, account_currency: e.target.value })}
                data-testid="account-currency">
                {CURRENCIES_LIST.map((c) => <option key={c.code} value={c.code}>{c.symbol} {c.code} — {c.label}</option>)}
              </select>
              <div className="text-[11px] text-white/40 mt-1">Utilisée pour tous les calculs de P&amp;L réels.</div>
            </div>
            <div>
              <label className="label">Fuseau horaire</label>
              <select className="field"
                value={s.timezone}
                onChange={(e) => setS({ ...s, timezone: e.target.value })}
                data-testid="timezone-select">
                {tzList.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
              <div className="text-[11px] text-white/40 mt-1">Kill Zones affichées dans votre heure locale.</div>
            </div>
            <div>
              <label className="label">Région (weekend forex)</label>
              <select className="field"
                value={s.region}
                onChange={(e) => setS({ ...s, region: e.target.value })}
                data-testid="region-select">
                {regions.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <div className="text-[11px] text-white/40 mt-1">Moyen-Orient : marché ouvert dim-jeu.</div>
            </div>
            <div>
              <label className="label">Mode de conformité</label>
              <select className="field"
                value={s.compliance_mode}
                onChange={(e) => setS({ ...s, compliance_mode: e.target.value })}
                data-testid="compliance-select">
                {Object.entries(compModes).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
              </select>
              <div className="text-[11px] text-white/40 mt-1">{compModes[s.compliance_mode]?.description || ""}</div>
            </div>
          </div>
        </Section>

        <Section title="Source de données de marché (Twelve Data)" desc={
          <>Récupérez votre clé gratuite sur <a href="https://twelvedata.com/register" target="_blank" rel="noreferrer" className="text-accent-blue underline">twelvedata.com/register</a>. 800 requêtes/jour gratuites, forex + crypto + indices + métaux couverts. Sans clé, l'application fonctionne en mode démo (prix synthétiques).</>
        }>
          <div>
            <label className="label">Clé API Twelve Data {s.twelve_data_api_key_masked && <span className="text-white/40 font-mono ml-2">actuelle : {s.twelve_data_api_key_masked}</span>}</label>
            <input className="field" type="password"
              value={s.twelve_data_api_key}
              onChange={(e) => setS({ ...s, twelve_data_api_key: e.target.value })}
              placeholder={s.twelve_data_api_key_masked ? "Laisser vide pour conserver la clé actuelle" : "ex : 1a2b3c4d5e6f..."}
              data-testid="td-key" />
            <div className="text-[11px] text-white/40 mt-1.5">Stockée côté serveur uniquement. Ne quitte jamais votre navigateur en clair.</div>
          </div>
        </Section>

        <Section title="Telegram" desc="Créez un bot via @BotFather sur Telegram pour obtenir un token, puis démarrez une conversation avec lui pour récupérer votre Chat ID (visitez https://api.telegram.org/bot<TOKEN>/getUpdates).">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label">Bot Token {s.telegram_bot_token_masked && <span className="text-white/40 font-mono ml-2">actuel : {s.telegram_bot_token_masked}</span>}</label>
              <input className="field" type="password"
                value={s.telegram_bot_token}
                onChange={(e) => setS({ ...s, telegram_bot_token: e.target.value })}
                placeholder={s.telegram_bot_token_masked ? "Laisser vide pour conserver" : "123456:ABC..."}
                data-testid="tg-token" />
            </div>
            <div>
              <label className="label">Chat ID</label>
              <input className="field"
                value={s.telegram_chat_id}
                onChange={(e) => setS({ ...s, telegram_chat_id: e.target.value })}
                placeholder="123456789"
                data-testid="tg-chatid" />
            </div>
          </div>
          <button className="btn mt-3" onClick={() => test("telegram")} disabled={testing === "telegram"} data-testid="test-telegram-btn">
            {testing === "telegram" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Envoyer un test Telegram
          </button>
        </Section>

        <Section title="Email" desc="Nécessite SENDGRID_API_KEY côté serveur, ou une configuration SMTP (SMTP_HOST, SMTP_USER, SMTP_PASSWORD, SMTP_FROM).">
          <div>
            <label className="label">Adresse de réception</label>
            <input className="field"
              value={s.email_recipient}
              onChange={(e) => setS({ ...s, email_recipient: e.target.value })}
              placeholder="vous@exemple.com"
              data-testid="email-recipient" />
          </div>
          <button className="btn mt-3" onClick={() => test("email")} disabled={testing === "email"} data-testid="test-email-btn">
            {testing === "email" ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Envoyer un test email
          </button>
        </Section>

        <Section title="Watchlist & timeframe par défaut" desc="Les actifs de la watchlist s'affichent dans la vue synthétique.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="label">Actifs (séparés par des virgules)</label>
              <input className="field"
                value={watchStr}
                onChange={(e) => setWatchStr(e.target.value)}
                placeholder="EURUSD, XAUUSD, BTCUSD..."
                data-testid="watchlist-input" />
            </div>
            <div>
              <label className="label">Unité de temps</label>
              <select className="field"
                value={s.default_timeframe}
                onChange={(e) => setS({ ...s, default_timeframe: e.target.value })}
                data-testid="default-timeframe">
                {TIMEFRAMES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
        </Section>

        <div className="pt-2">
          <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="save-settings-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
          </button>
        </div>
      </div>

      <ChangePasswordSection />
    </div>
  );
}

function PwdField({ label, value, onChange, show, setShow, testid }) {
  return (
    <div>
      <label className="label">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="field w-full pr-10"
          minLength={8}
          required
          data-testid={testid}
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white p-1"
          tabIndex={-1}
          data-testid={`${testid}-toggle`}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

function ChangePasswordSection() {
  const [current, setCurrent] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) {
      toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères");
      return;
    }
    if (newPwd !== confirm) {
      toast.error("Les deux nouveaux mots de passe ne correspondent pas");
      return;
    }
    if (newPwd === current) {
      toast.error("Le nouveau mot de passe doit être différent de l'actuel");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/users/change-password`,
        { current_password: current, new_password: newPwd });
      toast.success("Mot de passe changé avec succès");
      setCurrent(""); setNewPwd(""); setConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="panel p-6 mt-6" data-testid="change-password-panel">
      <div className="panel-title text-[14px] mb-1">Changer le mot de passe</div>
      <div className="text-xs text-white/50 mb-4">
        Utilisez un mot de passe unique et fort (min. 8 caractères).
        Cliquez sur l&apos;icône œil pour afficher/masquer les caractères tapés.
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
        <PwdField
          label="Mot de passe actuel"
          value={current} onChange={setCurrent}
          show={showCurrent} setShow={setShowCurrent}
          testid="pwd-current-input"
        />
        <PwdField
          label="Nouveau mot de passe"
          value={newPwd} onChange={setNewPwd}
          show={showNew} setShow={setShowNew}
          testid="pwd-new-input"
        />
        <PwdField
          label="Confirmer le nouveau mot de passe"
          value={confirm} onChange={setConfirm}
          show={showConfirm} setShow={setShowConfirm}
          testid="pwd-confirm-input"
        />
        <div className="pt-2">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || !current || !newPwd || !confirm}
            data-testid="change-password-submit"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Changer le mot de passe
          </button>
        </div>
      </form>

      <AccountDataSection />
    </div>
  );
}

function Section({ title, desc, children }) {
  return (
    <div className="border-b border-white/5 last:border-0 pb-5 last:pb-0">
      <div className="panel-title text-[14px]">{title}</div>
      {desc && <div className="text-xs text-white/50 mt-1 mb-3">{desc}</div>}
      {children}
    </div>
  );
}
